<template>
  <div class="reservas-history">
    <h2 class="subheading grey--text">Historial de Reservas y Códigos QR</h2>

    <v-container class="my-5">

      <v-alert v-if="error" type="warning" outline transition="scale-transition" class="mb-3">
        {{ error }}
      </v-alert>

      <v-layout align-center>
        <v-flex>
          <v-card class="elevation-5">
            <v-toolbar dark color="primary" class="mb-2">
              <v-icon class="mr-2">history</v-icon>
              <v-toolbar-title>Historial</v-toolbar-title>
            </v-toolbar>

            <v-card-text>
              <!-- Filtros -->
              <v-layout row wrap align-center class="mb-3">
                <v-flex xs12 sm6 md2 class="pr-2">
                  <v-select
                    v-model="filterType"
                    :items="filterTypeOptions"
                    label="Tipo"
                    prepend-icon="category"
                    clearable
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 md2 class="pr-2">
                  <v-select
                    v-model="filterStatus"
                    :items="filterStatusOptions"
                    label="Estado"
                    prepend-icon="filter_list"
                    clearable
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 md3 class="pr-2">
                  <v-text-field
                    v-model="searchText"
                    label="Buscar"
                    prepend-icon="search"
                    clearable
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md2 class="pr-2">
                  <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false" offset-y lazy>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(dateRange.start)"
                        label="Desde"
                        prepend-icon="calendar_today"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="dateRange.start = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateRange.start" @input="startMenu = false"></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false" offset-y lazy>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(dateRange.end)"
                        label="Hasta"
                        prepend-icon="calendar_today"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="dateRange.end = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateRange.end" @input="endMenu = false"></v-date-picker>
                  </v-menu>
                </v-flex>
              </v-layout>
            </v-card-text>

            <!-- Tabla -->
            <v-data-table
              :headers="headers"
              :items="filteredHistory"
              :loading="loading"
              :pagination.sync="pagination"
              :rows-per-page-text="'Filas por página'"
              :rows-per-page-items="[10, 25, 50, { text: 'Todos', value: -1 }]"
              :no-data-text="'No hay historial disponible'"
              class="elevation-1"
            >
              <template v-slot:items="props">
                <!-- Tipo -->
                <td>
                  <v-chip :color="getTypeColor(props.item.type)" text-color="white" small>{{ formatType(props.item.type) }}</v-chip>
                </td>
                <!-- Empresa -->
                <td>
                  <span class="font-weight-medium">{{ props.item.company_name }}</span>
                </td>
                <!-- Detalles -->
                <td>
                  <span class="body-2">{{ props.item.details }}</span>
                </td>
                <!-- Estado -->
                <td>
                  <v-chip :color="getStatusColor(props.item.status)" text-color="white" small>{{ formatStatus(props.item.status) }}</v-chip>
                </td>
                <!-- Fecha -->
                <td>
                  <span class="font-weight-medium">{{ formatDate(props.item.date) }}</span>
                  <br>
                  <span class="caption grey--text">{{ formatTime(props.item.date) }}</span>
                </td>
                <!-- Acciones -->
                <td class="justify-center layout px-0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon medium v-on="on" @click="viewDetails(props.item)">visibility</v-icon>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Dialog: Detalles -->
      <v-dialog v-model="dialogDetails" max-width="550px">
        <v-card v-if="selectedItem">
          <v-toolbar dark color="primary">
            <v-icon class="mr-2">info</v-icon>
            <v-toolbar-title>Detalles</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon color="red" @click="dialogDetails = false">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pa-4">
            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  :value="formatType(selectedItem.type)"
                  label="Tipo"
                  prepend-icon="category"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  :value="formatStatus(selectedItem.status)"
                  label="Estado"
                  prepend-icon="flag"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  :value="selectedItem.company_name"
                  label="Empresa"
                  prepend-icon="business"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  :value="selectedItem.details"
                  label="Detalles"
                  prepend-icon="notes"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12 class="mb-3">
                <v-divider class="mb-2"></v-divider>
                <div class="subheading grey--text text--darken-1 font-weight-bold mb-2">Fechas de Creación</div>
              </v-flex>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  :value="formatDate(selectedItem.date)"
                  label="Fecha de Creación"
                  prepend-icon="event"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  :value="formatTime(selectedItem.date)"
                  label="Hora de Creación"
                  prepend-icon="access_time"
                  readonly
                ></v-text-field>
              </v-flex>

              <v-flex xs12 class="mb-3 mt-2" v-if="selectedItem.start_date || selectedItem.end_date">
                <v-divider class="mb-2"></v-divider>
                <div class="subheading grey--text text--darken-1 font-weight-bold mb-2">Período de Reserva</div>
              </v-flex>
              <v-flex xs12 sm6 class="pr-2" v-if="selectedItem.start_date">
                <v-text-field
                  :value="formatDate(selectedItem.start_date)"
                  label="Fecha Inicio"
                  prepend-icon="event_note"
                  readonly
                  class="primary--text"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2" v-if="selectedItem.start_time">
                <v-text-field
                  :value="selectedItem.start_time"
                  label="Hora Inicio"
                  prepend-icon="schedule"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pr-2" v-if="selectedItem.end_date">
                <v-text-field
                  :value="formatDate(selectedItem.end_date)"
                  label="Fecha Fin"
                  prepend-icon="event_available"
                  readonly
                  class="error--text"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2" v-if="selectedItem.end_time">
                <v-text-field
                  :value="selectedItem.end_time"
                  label="Hora Fin"
                  prepend-icon="access_time"
                  readonly
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="dialogDetails = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'ReservationsHistory',
  computed: {
    ...mapGetters(['getProfile'])
  },
  data() {
    return {
      loading: false,
      error: null,

      dialogDetails: false,
      selectedItem: null,

      filterType: null,
      filterStatus: null,
      searchText: '',
      dateRange: {
        start: null,
        end: null
      },
      startMenu: false,
      endMenu: false,

      pagination: {
        sortBy: 'date',
        descending: true,
        page: 1,
        rowsPerPage: 10
      },

      headers: [
        { text: 'Tipo', value: 'type', width: '12%' },
        { text: 'Empresa', value: 'company', width: '20%' },
        { text: 'Detalles', value: 'details', width: '30%' },
        { text: 'Estado', value: 'status', width: '12%' },
        { text: 'Fecha', value: 'date', width: '18%' },
        { text: 'Acciones', value: 'actions', width: '8%', sortable: false }
      ],

      filterTypeOptions: [
        { text: 'Reserva', value: 'reservation' },
        { text: 'Código QR', value: 'qr_code' }
      ],

      filterStatusOptions: [
        { text: 'Activo', value: 'active' },
        { text: 'Expirado', value: 'expired' },
        { text: 'Cancelado', value: 'canceled' },
        { text: 'Usado', value: 'used' }
      ],

      history: []
    }
  },
  computed: {
    filteredHistory() {
      let filtered = this.history

      if (this.filterType) {
        filtered = filtered.filter(h => h.type === this.filterType)
      }

      if (this.filterStatus) {
        filtered = filtered.filter(h => h.status === this.filterStatus)
      }

      if (this.searchText) {
        const search = this.searchText.toLowerCase()
        filtered = filtered.filter(h =>
          h.company_name.toLowerCase().includes(search) ||
          h.details.toLowerCase().includes(search)
        )
      }

      if (this.dateRange.start) {
        filtered = filtered.filter(h =>
          new Date(h.date) >= new Date(this.dateRange.start)
        )
      }

      if (this.dateRange.end) {
        filtered = filtered.filter(h =>
          new Date(h.date) <= new Date(this.dateRange.end)
        )
      }

      return filtered
    }
  },
  watch: {
    // Recarga el historial cuando cambian los filtros de fecha
    'dateRange.start'() {
      this.fetchHistory()
    },
    'dateRange.end'() {
      this.fetchHistory()
    },
    // Filtros reactivos
    filterType() {
      // Los filtros se aplican en el computed property
    },
    filterStatus() {
      // Los filtros se aplican en el computed property
    }
  },
  created() {
    this.fetchHistory()
  },
  methods: {
    async fetchHistory() {
      this.loading = true
      this.error = null

      try {
        const historyItems = []

        // Construir parámetros de fecha si están disponibles
        const params = {}
        if (this.dateRange.start) {
          params.date_from = this.dateRange.start
        }
        if (this.dateRange.end) {
          params.date_to = this.dateRange.end
        }

        // Obtener reservas recibidas (role=beneficiary)
        try {
          const receivedConfig = {
            method: 'get',
            url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/list/',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + this.$store.getters.getToken
            },
            params: {
              role: 'beneficiary',
              ...params
            }
          }
          const receivedResponse = await axios(receivedConfig)
          const receivedReservations = Array.isArray(receivedResponse.data) ? receivedResponse.data : []
          
          receivedReservations.forEach(reservation => {
            historyItems.push({
              type: 'reservation',
              company_name: reservation.owner_company_name,
              details: `Reserva para ${reservation.beneficiary_company_name} - ${reservation.reserved_spaces.length} cajones`,
              status: reservation.status,
              date: reservation.created_at || reservation.start_date,
              start_date: reservation.start_date,
              end_date: reservation.end_date,
              start_time: reservation.start_time,
              end_time: reservation.end_time,
              role: 'beneficiary'
            })
          })
        } catch (err) {
          console.error('Error al cargar reservas recibidas:', err)
        }

        // Obtener reservas enviadas (role=owner)
        try {
          const sentConfig = {
            method: 'get',
            url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/list/',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + this.$store.getters.getToken
            },
            params: {
              role: 'owner',
              ...params
            }
          }
          const sentResponse = await axios(sentConfig)
          const sentReservations = Array.isArray(sentResponse.data) ? sentResponse.data : []
          
          sentReservations.forEach(reservation => {
            historyItems.push({
              type: 'reservation',
              company_name: reservation.beneficiary_company_name,
              details: `Reserva desde ${reservation.owner_company_name} - ${reservation.reserved_spaces.length} cajones`,
              status: reservation.status,
              date: reservation.created_at || reservation.start_date,
              start_date: reservation.start_date,
              end_date: reservation.end_date,
              start_time: reservation.start_time,
              end_time: reservation.end_time,
              role: 'owner'
            })
          })
        } catch (err) {
          console.error('Error al cargar reservas enviadas:', err)
        }

        // Obtener códigos QR
        try {
          const codesConfig = {
            method: 'get',
            url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/list/',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + this.$store.getters.getToken
            }
          }
          const codesResponse = await axios(codesConfig)
          const codes = Array.isArray(codesResponse.data) ? codesResponse.data : []
          
          codes.forEach(code => {
            historyItems.push({
              type: 'qr_code',
              company_name: code.provider_name,
              details: `Código QR para cajón ${code.space_number}`,
              status: code.qr_status,
              date: code.activated_at || code.invitation_date,
              code: code.qr_code
            })
          })
        } catch (err) {
          console.error('Error al cargar códigos QR:', err)
        }

        // Ordenar por fecha descendente
        this.history = historyItems.sort((a, b) => new Date(b.date) - new Date(a.date))
      } catch (err) {
        this.error = 'Error al cargar historial'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    viewDetails(item) {
      this.selectedItem = item
      this.dialogDetails = true
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },

    formatTime(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('HH:mm')
    },

    formatType(type) {
      const map = {
        reservation: 'Reserva',
        qr_code: 'Código QR'
      }
      return map[type] || type
    },

    formatStatus(status) {
      const map = {
        active: 'Activo',
        expired: 'Expirado',
        canceled: 'Cancelado',
        used: 'Usado'
      }
      return map[status] || status
    },

    getTypeColor(type) {
      const map = {
        reservation: 'purple',
        qr_code: 'blue'
      }
      return map[type] || 'grey'
    },

    getStatusColor(status) {
      const map = {
        active: 'green',
        expired: 'orange',
        canceled: 'red',
        used: 'blue'
      }
      return map[status] || 'grey'
    }
  }
}
</script>

